import classNames from "classnames";
import { SvgClose } from "components/svg";
import React, { type PropsWithChildren, type RefObject, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";

import { v4 as uuidv4 } from "uuid";

export type PopupProps = PropsWithChildren<{
  fullscreen?: boolean;
  noHeader?: boolean;
  onClose: () => void;
  fixSize?: string;

  closeOnOutsideClick?: boolean;
  refOpener?: RefObject<HTMLElement>;
  title?: string;
  coloredTitle?: boolean;
  wrapTitle?: boolean;
  portal?: boolean;
  noHorizontalLine?: boolean;
}>;

const Popup: React.FC<PopupProps> = ({
  fullscreen = false,
  noHeader = false,
  onClose,
  closeOnOutsideClick = false,
  children,
  title = "",
  fixSize,
  coloredTitle = false,
  wrapTitle = false,
  portal,
  noHorizontalLine = false
}) => {
  const [guid] = useState(uuidv4()); //NOSONAR
  const containerRef = useRef<HTMLDivElement>(null);

  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!portal) return;

    const popupRoot = document.getElementById("popup-root");
    if (!popupRoot) return;

    const countAttr = "data-local-popup-count";
    const currentCount = Number(popupRoot.getAttribute(countAttr) || "0");
    popupRoot.setAttribute(countAttr, String(currentCount + 1));
    popupRoot.classList.add("open");

    return () => {
      const nextCount = Math.max(0, Number(popupRoot.getAttribute(countAttr) || "1") - 1);
      if (nextCount === 0) {
        popupRoot.removeAttribute(countAttr);
        popupRoot.classList.remove("open");
      } else {
        popupRoot.setAttribute(countAttr, String(nextCount));
      }
    };
  }, [portal]);

  useEffect(() => {
    if (!closeOnOutsideClick) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (event.target === containerRef.current) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keyup", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keyup", handleEscape);
    };
  }, [closeOnOutsideClick, onClose]);

  let innerContent = (
    <div ref={elementRef} className={"handle_" + guid} >
      {!noHeader && (
        <group

          {...(coloredTitle
            ? { "data-background": "main", "data-color": "white" }
            : { "data-background": "main-background" })}
          data-space="20"
          //data-space-horizontal="40"
          data-space-bottom="5"
          data-gap="5"

        >
          {wrapTitle ?
            <group data-wrap="no" data-align="center" data-height="auto" >
              <text
                data-weight="400"
                data-color="main"
                data-text-size="x-large"
                data-name="popup_title"
                data-wrap="wrap"
                data-max-length='700'
              >
                {title}
              </text>
              <group
                data-color="secondary"
                className="close"
                data-width="auto"
                data-position="right"
                data-cursor="pointer"
                data-interactive=""
                data-transparent=""
                data-radius="5"
                onClick={onClose}
              >
                <icon data-svg-color="secondary" data-svg-size="12" data-length="30" data-height="30" data-space="5"><SvgClose></SvgClose></icon>
              </group>
            </group>
            :
            <group data-wrap="no" data-align="center"  >

              <text
                data-weight="400"
                //data-space="10"
                data-ellipsis=""
                data-color="main"
                data-text-size="x-large"
                data-name="popup_title"
              >
                {title}
              </text>
              <group
                data-color="secondary"
                className="close"
                data-width="auto"
                data-position="right"
                data-cursor="pointer"
                data-interactive=""
                data-transparent=""
                data-radius="5"
                onClick={onClose}
              >
                <icon data-svg-color="secondary" data-svg-size="12" data-length="30" data-height="30" data-space="5"><SvgClose></SvgClose></icon>
              </group>
            </group>
          }
          {!noHorizontalLine && <separator data-horizontal="" data-margin="none"></separator>}
        </group>
      )}
      <div>
        <div
          id="windowContent"
          className="window_cont"
          fixed={fixSize} //NOSONAR
        >
          {children}
        </div>
      </div>
    </div>
  );
  if (!fullscreen) {
    innerContent = (
      <Draggable
        nodeRef={elementRef as any}
        cancel=".window_cont,.close"
        bounds="parent"
        handle={".handle_" + guid}
      >
        {innerContent}
      </Draggable>
    );
  }

  const content = (
    <div
      ref={containerRef}
      className={classNames("popup", "open", { fullscreen })}
    >
      {innerContent}
    </div>
  );

  if (!portal) {
    return content;
  }

  return createPortal(
    content,
    document.getElementById("popup-root") as HTMLElement
  );
};

export default Popup;
